import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Camera, Volume2, Heart } from 'lucide-react';

const ExperienceSection = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '200px 0px', // Start much earlier for mobile
  });



  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  // Workshop experience images
  const experienceImages = [
    {
      title: "תפאורה קסומה",
      description: "תאורה מיוחדת ותפאורה שיוצרות אווירה בלתי נשכחת",
      image: "/images/WhatsApp Image 2025-08-11 at 10.16.19.jpeg"
    },
    {
      title: "חוויה באוזניות",
      description: "כל משתתף חווה מסע קולי אישי ומרגש",
      image: "/images/WhatsApp Image 2025-08-11 at 10.16.20.jpeg"
    },
    {
      title: "יצירה אישית",
      description: "בניית לוח החזון עם הקיט האישי",
      image: "/images/WhatsApp Image 2025-08-11 at 10.16.21.jpeg"
    },
    {
      title: "רגעי קסם",
      description: "התרגשות, גילוי ויצירה בקבוצה",
      image: "/images/WhatsApp Image 2025-08-11 at 10.16.20 (2).jpeg"
    }
  ];



  return (
    <section className="section-padding bg-gradient-to-br from-dream-soft-beige via-dream-pink-beige to-dream-light-beige relative overflow-hidden text-gray-800">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-40 h-40 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-20 left-20 w-32 h-32 bg-gradient-to-r from-dream-pink-beige to-dream-soft-beige rounded-full blur-2xl opacity-20"></div>
        <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-lg opacity-15"></div>
      </div>

      <motion.div
        ref={ref}
        className="container-width relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-dream-teal to-dream-warm-brown px-6 py-3 rounded-full text-white font-bold text-lg shadow-lg">
              🎧 סדנת חזון הראשונה מסוגה בארץ – עם אוזניות! 🎧
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            חוויית <span className="gradient-text">החושים</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            במהלך הסדנה, נעבוד על חמישה חושים שיקרבו אותנו למגנט החזון האישי שלכם
          </p>
        </motion.div>

        {/* Experience Gallery */}
        <motion.div variants={itemVariants} className="mb-16">
          <h3 className="text-3xl font-bold text-gray-800 mb-12 text-center">רגעים מהסדנה</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {experienceImages.map((image, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ y: 50, opacity: 0 }}
                animate={inView ? { y: 0, opacity: 1 } : { y: 50, opacity: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}

              >
                <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-700 ease-out group">
                  {/* Full image background */}
                  <div className="relative h-48 overflow-hidden">
                    <motion.img 
                      src={image.image} 
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      animate={{ 
                        scale: [1, 1.02, 1]
                      }}
                      transition={{ 
                        duration: 4 + index * 0.5, 
                        repeat: Infinity, 
                        ease: "easeInOut" 
                      }}
                    />
                    {/* Overlay gradient for text readability */}
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 ${
                      index === 0 ? 'group-hover:from-dream-teal/30' :
                      index === 1 ? 'group-hover:from-dream-warm-brown/30' :
                      index === 2 ? 'group-hover:from-dream-pink-beige/30' :
                      'group-hover:from-dream-teal/30'
                    } transition-all duration-500`}></div>
                    
                    {/* Floating elements overlay */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      animate={{
                        rotate: index % 2 === 0 ? [0, 360] : [360, 0]
                      }}
                      transition={{
                        duration: 20 + index * 5,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    >
                      {[...Array(3)].map((_, i) => (
                        <motion.div
                          key={i}
                          className={`absolute w-1 h-1 rounded-full ${
                            index === 0 ? 'bg-white' :
                            index === 1 ? 'bg-white' :
                            index === 2 ? 'bg-white' :
                            'bg-white'
                          } opacity-70`}
                          style={{
                            top: `${20 + i * 25}%`,
                            left: `${10 + i * 30}%`,
                          }}
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 0.8, 0.3]
                          }}
                          transition={{
                            duration: 2 + i * 0.5,
                            repeat: Infinity,
                            delay: index * 0.5 + i * 0.3
                          }}
                        />
                      ))}
                    </motion.div>
                  </div>
                  
                  {/* Content section */}
                  <div className="p-6 relative z-10">
                    {/* Title section */}
                    <div className="mb-3">
                      <h4 className="font-bold text-xl text-gray-800 group-hover:text-gray-900 transition-colors duration-300 text-center">
                        {image.title}
                      </h4>
                    </div>
                    
                    {/* Description section */}
                    <div className="text-center">
                      <p className="text-gray-600 text-base leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                        {image.description}
                      </p>
                    </div>
                  </div>
                  

                  
                  {/* Particle effect on hover */}
                  <motion.div
                    className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    initial={{ scale: 0, rotate: 0 }}
                    whileHover={{ scale: 1, rotate: 360 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  >
                    <motion.div
                      className={`w-3 h-3 rounded-full ${
                        index === 0 ? 'bg-dream-teal' :
                        index === 1 ? 'bg-dream-warm-brown' :
                        index === 2 ? 'bg-dream-pink-beige' :
                        'bg-dream-soft-beige'
                      } shadow-lg`}
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.6, 1, 0.6]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Atmosphere Description */}
        <motion.div variants={itemVariants} className="text-center">
          <div className="bg-white bg-opacity-80 backdrop-blur-sm p-8 rounded-3xl max-w-5xl mx-auto shadow-lg">
            <div className="flex justify-center mb-6">
              <div className="flex gap-3">
                <Volume2 className="text-dream-teal" size={24} />
                <Camera className="text-dream-warm-brown" size={24} />
                <Heart className="text-dream-pink-beige" size={24} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-6">אלמנטים חושיים לרוגע ולפתיחת השפע</h3>
            <div className="grid md:grid-cols-2 gap-8 text-right">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  הסדנה מועברת בצורה ייחודית באמצעות אוזניות, עם תפאורה שמעוררת את הדמיון 
                  ויוצרת אווירה מיוחדת שמכניסה למצב של יצירתיות, חלומות והשראה.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  במהלך הסדנה מתבצע חיבור לחושים עם תאורה מיוחדת בפנים, שמאפשר לכל משתתף 
                  לחוות את המסע שלו באופן עמוק ואותנטי.
                </p>
              </div>
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  כל אחד מקבל "קיט" אישי לידו לבניית לוח החזון שלו, הכולל כלים יצירתיים 
                  המותאמים אישית לתהליך.
                </p>
                <p className="text-dream-warm-brown font-semibold text-lg">
                  הסדנה מתאימה גם לפורומים אישיים וגם קבוצתיים, ומגיעה עד אליכם – 
                  מינימום 20 משתתפים.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ExperienceSection;
